import React, { createContext, useContext, useEffect, useMemo } from "react";
import { useClubAttention } from "./ClubAttentionContext";

const ClubRequestUnreadContext = createContext(null);

const clampInt = (value) => {
  const next = Number(value);
  if (!Number.isFinite(next)) return 0;
  return Math.max(0, Math.floor(next));
};

const normalizeMeta = (rawMeta) => {
  if (rawMeta && typeof rawMeta === "object") return rawMeta;
  if (typeof rawMeta !== "string") return {};
  try {
    const parsed = JSON.parse(rawMeta);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const EMPTY_CONTEXT = {
  hasLoaded: false,
  totalPending: 0,
  pendingClubsCount: 0,
  byClubId: {},
  clubMetaById: {},
  refetch: async () => {},
};

export const ClubRequestUnreadProvider = ({ children }) => {
  const { data: attentionData, refetch } = useClubAttention();
  const data = attentionData?.join_requests;

  useEffect(() => {
    const handleRefresh = () => {
      void refetch();
    };
    const handleNotification = (event) => {
      const meta = normalizeMeta(event?.detail?.meta);
      const actionKind = String(meta?.action_kind || meta?.actionKind || "").trim().toLowerCase();
      if (actionKind === "club_join_request") handleRefresh();
    };

    window.addEventListener("vg:notification", handleNotification);
    return () => {
      window.removeEventListener("vg:notification", handleNotification);
    };
  }, [refetch]);

  const value = useMemo(() => {
    const byClubId = {};
    const clubMetaById = {};
    const clubs = Array.isArray(data?.clubs) ? data.clubs : [];

    clubs.forEach((item) => {
      const clubId = item?.club_id != null ? String(item.club_id) : null;
      if (!clubId) return;
      const pendingCount = clampInt(item?.pending_count);
      byClubId[clubId] = pendingCount;
      clubMetaById[clubId] = {
        clubId: Number(item?.club_id || 0) || null,
        clubName: String(item?.club_name || ""),
        publicPath: String(item?.public_path || ""),
        pendingCount,
      };
    });

    return {
      hasLoaded: data !== undefined,
      totalPending: clampInt(data?.total_pending),
      pendingClubsCount: clampInt(data?.pending_clubs_count),
      byClubId,
      clubMetaById,
      refetch,
    };
  }, [data, refetch]);

  return (
    <ClubRequestUnreadContext.Provider value={value}>
      {children}
    </ClubRequestUnreadContext.Provider>
  );
};

export const useClubRequestUnread = () => {
  const context = useContext(ClubRequestUnreadContext);
  return context || EMPTY_CONTEXT;
};
